import { createContext, useContext, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

export const ThemeProviderCustom = ({ children }) => {
  const theme = useMemo(() => createTheme({
    palette: {
      mode: 'light',
      primary: { main: '#F5A623', light: '#FFD180', dark: '#E09500', contrastText: '#fff' },
      secondary: { main: '#333', light: '#555', dark: '#111' },
      background: { default: '#FAFAFA', paper: '#fff' },
      text: { primary: '#1a1a1a', secondary: '#666' },
      success: { main: '#4CAF50' },
      error: { main: '#f44336' },
      warning: { main: '#FF9800' },
      info: { main: '#2196F3' },
      divider: '#eee',
    },
    typography: {
      fontFamily: ['Inter', 'Roboto', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Arial', 'sans-serif'].join(','),
      button: { textTransform: 'none', fontWeight: 600 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 600 },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, textTransform: 'none', fontWeight: 600, padding: '8px 20px' },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 2px 8px rgba(245,166,35,0.3)' }
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': { borderWidth: '1.5px' }
          }
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            border: '1px solid #f0f0f0'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 14 },
          elevation1: { boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: 'none' }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500 }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#F5A623'
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#F5A623'
            }
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': { fontWeight: 700, color: '#F5A623' }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: '#F5A623' }
        }
      }
    }
  }), []);

  return (
    <ThemeContext.Provider value={{ mode: 'light', toggleTheme: () => {}, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeMode must be used within ThemeProviderCustom');
  return context;
};
